<template>
  <!-- Language Settings   -->
  <v-card
    class="px-3 py-3"
    elevation="10"
    height="100%"
  >
    <CardTitleWithTooltip>
      <template v-slot:title>
        {{ $t('customer.settings.language.title') }}
      </template>
      <template v-slot:tooltip>
        {{ $t('customer.settings.language.titleTooltip') }}
      </template>
    </CardTitleWithTooltip>
    
    <v-card-text class="pa-0">
      <v-container class="py-0 px-2">
        <v-row>
          <v-col cols="12" class="d-flex py-0">
            <label class="font-weight-bold">{{ $t('customer.settings.language.adminPanel') }}</label>
          </v-col>
        </v-row>
        <v-row class="pb-4"> 
          <v-col cols="12" class="py-0">
            <v-combobox
              v-model="adminLanguage"            
              :items="adminItems"
              item-text="locale" 
              item-value="code"
              return-object
              hide-details
              dense
              outlined
              solo
            ></v-combobox>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="d-flex py-0">
            <label class="font-weight-bold">{{ $t('customer.settings.language.agentWidget') }}</label>
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col cols="12" class="py-0">
            <v-combobox
              v-model="agentLanguage"            
              :items="agentItems"
              item-text="locale" 
              item-value="code"
              return-object
              hide-details
              dense
              outlined
              solo
            ></v-combobox>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="pt-2 pb-2 px-4 justify-center">
      <v-btn
        @click="dialogSaveLanguageConfirm = true"
        v-bind="size"
        color="primary"
        dark
      >
        {{ $t('save') }}
      </v-btn>
      <ConfirmDialog
        v-if="dialogSaveLanguageConfirm"
        :confirmDialogShow="dialogSaveLanguageConfirm"
        @confirm-dialog-close="dialogSaveLanguageConfirm = false"
        @confirm-dialog-save="saveLanguages"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
  import { mapGetters, mapMutations, mapActions } from 'vuex'
  import customerOps from '../api/users'
  import resizeMixin from '../mixins/resizeMixins'
  import i18n from '../i18n';

  export default {
    mixins: [resizeMixin],
    components: {
      CardTitleWithTooltip: require('../components/CardTitleWithTooltip').default,
      ConfirmDialog: () => import('@/components/ConfirmDialog')
    },
    created() {
      // get Customer Profile data
      this.customerProfile = { ...this.getCustomerProfile }

      // load the languages data and Capitalize the locale value
      this.loadLanguages()
    },
    data: () => ({
      customerProfile: {},
      adminLanguage: {},
      adminItems: [
        {
          locale: 'English',
          code: 'en' 
        },
        {
          locale: 'Spanish',
          code: 'es'
        },
        {
          locale: 'Portuguese',
          code: 'pt'
        }
      ],
      agentLanguage: {},
      agentItems: [
        {
          locale: 'English',
          code: 'en' 
        },
        {
          locale: 'Spanish',
          code: 'es'
        },
        {
          locale: 'Portuguese',
          code: 'pt'
        }
      ],
      dialogSaveLanguageConfirm: false,
    }),
    computed: {
      ...mapGetters({
        getCustomerProfile: 'users/getCustomerProfile',
        getLanguageAdmin: 'users/getLanguageAdmin',
        getLanguageAgent: 'users/getLanguageAgent',
      }),
    },
    methods: {
      loadLanguages() {
        // load the languages data and Capitalize the locale value
        this.adminLanguage = { ...this.getLanguageAdmin }
        this.adminLanguage.locale = this.toCapitalCase(this.adminLanguage.locale)
        this.agentLanguage = { ...this.getLanguageAgent }
        this.agentLanguage.locale = this.toCapitalCase(this.agentLanguage.locale)
      },
      ...mapActions({
        setLanguageData: 'users/setLanguageData'
      }),
      ...mapMutations({
        setSnack: 'setSnack'
      }),
      showSnack: function (snack, isError) {
        const setSnackObj = {
          message: snack,
          isError: !!isError
        }
        this.setSnack(setSnackObj)
      },
      toCapitalCase: (value) => {
        if (!value) return ''
        value = value.toString()
        return value.charAt(0).toUpperCase() + value.slice(1)
      },
      saveLanguages() {
        this.dialogSaveLanguageConfirm = false

        // prepare language object to be saved
        const adminLanguage = {
          code: this.adminLanguage.code,
          locale: this.adminLanguage.locale.toLowerCase(),
          app: "navomi-admin-panel-crmwidget-web"
        }

        const agentLanguage = {
          code: this.agentLanguage.code,
          locale: this.agentLanguage.locale.toLowerCase(),
          app: "navomi-agent-crmwidget-web"
        }

        const language = []
        language.push(adminLanguage)
        language.push(agentLanguage)

        const languageStringified = JSON.stringify(language)

        const updateLanguageData = {
          strict: true,
          value: {
            op: "LANGUAGEUPDATE",
            id: this.customerProfile.id, // Admin collection id
            accountId: this.customerProfile.accountId,
            sessionId: this.customerProfile.sessionId,
            data: {
              language: languageStringified
            }
          }
        }

        customerOps.customerUpdateLanguageConfig(updateLanguageData).then(response => {
          // update the VUEX
          this.setLanguageData(languageStringified)

          // update the sessionStorage I18N locale
          localStorage.setItem('locale-admin', this.adminLanguage.code)

          // Snackbar Success
          this.showSnack(i18n.t('customer.settings.language.updateSuccess'), false);

          // update the sessionStorage user info
          // TODO modify store and sessionStorage to keep update data until refresh,after refresh
          const userDataFromStorage = sessionStorage.getItem('user');

          let userDataFromStorageParsed = JSON.parse(userDataFromStorage);
          let storageData = JSON.parse(sessionStorage.getItem('user'));
          storageData.value.data.language =  [ ...language ];
          sessionStorage.setItem("user", JSON.stringify(storageData));
        })
        .catch(err => {
          console.error(`[CustomerSettingsLanguage] - saveLanguages - error: `, err)

          // Snackbar error
          this.showSnack(i18n.t('customer.settings.language.updateError'), true);
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
  label {
    font-size: 16px;
  }
</style>